import React, { useState } from "react";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import { Divider, TextField, Button, IconButton, Snackbar, Alert } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import PersonAddOutlinedIcon from "@mui/icons-material/PersonAddOutlined";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import CheckIcon from "@mui/icons-material/Check";
import LinkIcon from "@mui/icons-material/Link";

interface StudioInviteSidebarProps {
    inviteLink: string;
    onSendInvite?: (email: string) => Promise<void>;
}

const titleTextStyle = {
    color: 'var(--app-text-primary)',
    paddingLeft: '8px',
    paddingRight: '8px',
    flex: 1,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    fontSize: '14px',
    fontWeight: 500,
    lineHeight: '20px',
    fontFamily: 'Inter, sans-serif',
    userSelect: 'none',
};

const StudioInviteSidebar: React.FC<StudioInviteSidebarProps> = ({ 
    inviteLink,
    onSendInvite,
}) => {
    const theme = useTheme();
    const isDarkMode = theme.palette.mode === 'dark';
    const [copied, setCopied] = useState(false);
    const [email, setEmail] = useState('');
    const [sending, setSending] = useState(false);
    const [showSuccess, setShowSuccess] = useState(false);

    const handleCopyLink = async () => {
        try {
            await navigator.clipboard.writeText(inviteLink);
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        } catch (err) {
            console.error('Failed to copy link:', err);
        }
    };

    const handleSendInvite = async () => {
        if (!email || !onSendInvite) return;
        
        setSending(true);
        try {
            await onSendInvite(email);
            setEmail('');
            setShowSuccess(true);
        } catch (err) {
            console.error('Failed to send invite:', err);
        } finally {
            setSending(false);
        }
    };

    return (
        <Box sx={{ height: '100%', width: '100%', display: 'flex', flexDirection: 'column', gap: '8px', p: '3px' }}>
            {/* Header */}
            <Box sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center', marginLeft: '8px', gap: 1, marginTop: 1 }}>
                <Box sx={{ width: '24px', height: '24px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <PersonAddOutlinedIcon sx={{ fontSize: 20, color: 'var(--app-text-primary)' }} />
                </Box>
                <Box sx={titleTextStyle}>Invite</Box>
            </Box>

            <Divider sx={{ mx: 1 }} />

            {/* Content */}
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5, px: 1, pt: 1 }}>
                {/* Share link section */}
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                    <Typography sx={{ fontSize: 12, fontWeight: 500, color: 'var(--app-text-primary)' }}>
                        Share link
                    </Typography>
                    <Typography sx={{ fontSize: 12, color: 'var(--app-text-muted)', lineHeight: 1.5 }}>
                        Anyone with this link can join as a guest
                    </Typography>
                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1,
                            p: 1,
                            borderRadius: '8px',
                            backgroundColor: 'var(--app-bg-subtle)',
                            border: '1px solid var(--app-border-subtle)',
                        }}
                    >
                        <LinkIcon sx={{ fontSize: 16, color: 'var(--app-text-subtle)' }} />
                        <Typography
                            sx={{
                                flex: 1,
                                fontSize: 12,
                                color: 'var(--app-text-muted)',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                            }}
                        >
                            {inviteLink || 'Generating link...'}
                        </Typography>
                        <IconButton
                            size="small"
                            onClick={handleCopyLink}
                            disabled={!inviteLink}
                            sx={{
                                p: 0.5,
                                color: copied ? '#34D399' : 'var(--app-text-muted)',
                                '&:hover': { backgroundColor: 'var(--app-border-subtle)' },
                            }}
                        >
                            {copied ? <CheckIcon sx={{ fontSize: 16 }} /> : <ContentCopyIcon sx={{ fontSize: 16 }} />}
                        </IconButton>
                    </Box>
                    <Button
                        variant="contained"
                        fullWidth
                        onClick={handleCopyLink}
                        disabled={!inviteLink}
                        sx={{
                            mt: 0.5,
                            backgroundColor: 'var(--app-text-primary)',
                            color: 'var(--app-bg-surface)',
                            textTransform: 'none',
                            fontWeight: 500,
                            fontSize: 13,
                            borderRadius: '8px',
                            py: 1,
                            transition: 'filter 0.12s ease, opacity 0.12s ease',
                            '&:hover': {
                                backgroundColor: 'var(--app-text-primary)',
                                filter: 'brightness(0.9)',
                            },
                            '&:disabled': {
                                backgroundColor: 'var(--app-border-subtle)',
                                color: 'var(--app-text-subtle)',
                            },
                        }}
                    >
                        {copied ? 'Copied!' : 'Copy link'}
                    </Button>
                </Box>

                <Divider />

                {/* Email invite section */}
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                    <Typography sx={{ fontSize: 12, fontWeight: 500, color: 'var(--app-text-primary)' }}>
                        Invite by email
                    </Typography>
                    <TextField
                        placeholder="Enter email address"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        size="small"
                        fullWidth
                        sx={{
                            '& .MuiOutlinedInput-root': {
                                borderRadius: '8px',
                                fontSize: 13,
                                color: 'var(--app-text-primary)',
                                backgroundColor: 'var(--app-bg-subtle)',
                                '& fieldset': {
                                    borderColor: 'var(--app-border-subtle)',
                                },
                                '&:hover fieldset': {
                                    borderColor: 'var(--app-border-muted)',
                                },
                                '&.Mui-focused fieldset': {
                                    borderColor: '#3B82F6',
                                },
                            },
                            '& .MuiInputBase-input::placeholder': {
                                color: 'var(--app-text-subtle)',
                                opacity: 1,
                            },
                        }}
                    />
                    <Button
                        variant="outlined"
                        fullWidth
                        onClick={handleSendInvite}
                        disabled={!email || sending || !onSendInvite}
                        sx={{
                            borderColor: 'var(--app-border-subtle)',
                            color: 'var(--app-text-primary)',
                            backgroundColor: 'var(--app-bg-surface)',
                            textTransform: 'none',
                            fontWeight: 500,
                            fontSize: 13,
                            borderRadius: '8px',
                            py: 1,
                            transition: 'background-color 0.12s ease, border-color 0.12s ease',
                            '&:hover': {
                                borderColor: 'var(--app-border-muted)',
                                backgroundColor: 'var(--app-bg-subtle)',
                            },
                            '&:disabled': {
                                borderColor: 'var(--app-border-subtle)',
                                color: 'var(--app-text-subtle)',
                            },
                        }}
                    >
                        {sending ? 'Sending...' : 'Send invite'}
                    </Button>
                </Box>

                {/* Tips section */}
                <Box
                    sx={{
                        p: 1.5,
                        borderRadius: '8px',
                        backgroundColor: isDarkMode ? 'rgba(56, 189, 248, 0.12)' : '#F0F9FF',
                        border: `1px solid ${isDarkMode ? 'rgba(56, 189, 248, 0.35)' : '#BAE6FD'}`,
                    }}
                >
                    <Typography sx={{ fontSize: 11, color: isDarkMode ? '#93C5FD' : '#0369A1', fontWeight: 500, mb: 0.5 }}>
                        Tips for guests
                    </Typography>
                    <Typography sx={{ fontSize: 11, color: isDarkMode ? '#93C5FD' : '#0369A1', lineHeight: 1.5 }}>
                        • Use Chrome or Edge browser only {'\n'}
                    </Typography>
                    <Typography sx={{ fontSize: 11, color: isDarkMode ? '#93C5FD' : '#0369A1', lineHeight: 1.5 }}>
                        • Use headphones to avoid echo{'\n'}
                    </Typography>
                    <Typography sx={{ fontSize: 11, color: isDarkMode ? '#93C5FD' : '#0369A1', lineHeight: 1.5 }}>
                        • Videos in the room may appear lower quality than recordings, which are captured at full quality.
                    </Typography>
                </Box>
            </Box>

            {/* Success snackbar */}
            <Snackbar
                open={showSuccess}
                autoHideDuration={3000}
                onClose={() => setShowSuccess(false)}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            >
                <Alert severity="success" sx={{ width: '100%' }}>
                    Invite sent successfully!
                </Alert>
            </Snackbar>
        </Box>
    );
};

export default StudioInviteSidebar;
